import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Plus,
  Edit3,
  Trash2,
  Upload,
  ListOrdered,
  Search,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  X,
  FileSpreadsheet,
  RefreshCw,
  Settings2,
  ChevronLeft,
} from 'lucide-react'

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

const V2_FIELDS = [
  { key: 'sku', label: 'SKU', required: true },
  { key: 'name', label: 'Nombre', required: true },
  { key: 'price', label: 'Precio (con IVA)', required: false },
  { key: 'cost', label: 'Costo (sin IVA)', required: false },
  { key: 'stock', label: 'Stock', required: false },
  { key: 'brand_name', label: 'Marca (texto)', required: false },
  { key: 'line', label: 'Línea (max 10)', required: false },
  { key: 'compatible_models_text', label: 'Modelos compatibles', required: false, multi: true },
  { key: 'tags', label: 'Tags', required: false },
  { key: 'description', label: 'Descripción', required: false },
] as const

type V2Field = (typeof V2_FIELDS)[number]['key']
type ColumnMapping = Partial<Record<V2Field, string | string[] | null>>

interface TransformConfig {
  price_multiplier?: number
  stock_combine_columns?: string[]
  stock_combine_op?: 'sum' | 'max'
  normalize_sku?: 'trim' | 'upper' | 'trim_upper'
  skip_rows_without_sku?: boolean
}

interface SupplierListItem {
  id: string
  key: string
  name: string
  contactEmail: string | null
  contactPhone: string | null
  sheetName: string | null
  headerRow: number
  isActive: boolean
  importsCount: number
  lastImportAt: string | null
}

interface SupplierDetail {
  id: string
  key: string
  name: string
  contactEmail: string | null
  contactPhone: string | null
  defaultBrandId: string | null
  defaultCategoryId: string | null
  sheetName: string | null
  headerRow: number
  columnMapping: ColumnMapping
  transformConfig: TransformConfig | null
  skuPrefix: string | null
  isActive: boolean
  notes: string | null
}

interface SheetInfo {
  name: string
  columnsByHeaderRow: Record<number, string[]>
}

/* -------------------------------------------------------------------------- */
/* Root                                                                        */
/* -------------------------------------------------------------------------- */

export function Proveedores() {
  const [items, setItems] = useState<SupplierListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null) // detail view
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState<SupplierListItem | null>(null)

  async function load() {
    setLoading(true)
    try {
      const r = await fetch(`/api/admin/suppliers?q=${encodeURIComponent(q)}`)
      const d = await r.json()
      setItems(d.items || [])
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
  }, [])

  async function remove(s: SupplierListItem) {
    if (
      !confirm(
        `¿Eliminar el proveedor "${s.name}"?\n\nSi tiene productos o historial de imports puede fallar.`,
      )
    )
      return
    const r = await fetch(`/api/admin/suppliers/${s.id}`, { method: 'DELETE' })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      alert(d.error || 'No se pudo eliminar')
      return
    }
    load()
  }

  // Activa/desactiva el proveedor Y oculta/muestra TODOS sus productos en la web.
  // Solo afecta productos con supplier_id = este proveedor; los que no tienen
  // proveedor no se tocan.
  async function toggleActive(s: SupplierListItem) {
    const off = s.isActive // se va a apagar
    const msg = off
      ? `¿DESACTIVAR "${s.name}"?\n\nSe OCULTARÁN todos sus productos de la web pública.`
      : `¿ACTIVAR "${s.name}"?\n\nSe MOSTRARÁN de nuevo sus productos en la web.`
    if (!confirm(msg)) return
    // Optimista: refleja el cambio de inmediato en la lista.
    setItems((prev) =>
      prev.map((it) => (it.id === s.id ? { ...it, isActive: !off } : it)),
    )
    const r = await fetch(`/api/admin/suppliers/${s.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive: !off }),
    })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      alert(d.error || 'No se pudo cambiar el estado')
      load() // revertir al estado real
      return
    }
    const d = await r.json().catch(() => ({}))
    if (typeof d.productsAffected === 'number') {
      alert(
        `Proveedor ${off ? 'DESACTIVADO' : 'ACTIVADO'}.\n${d.productsAffected} producto(s) ${off ? 'ocultados de' : 'mostrados en'} la web.`,
      )
    }
    load()
  }

  /* Detail view */
  if (editingId) {
    return (
      <SupplierDetailView
        id={editingId}
        onBack={() => {
          setEditingId(null)
          load()
        }}
      />
    )
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
            <FileSpreadsheet className="size-6 text-brand-600" /> Proveedores
          </h1>
          <p className="text-sm text-ink-500">
            {items.length} proveedores · Configura el mapeo Excel sin tocar código
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          <Plus className="size-4" /> Nuevo proveedor
        </button>
      </header>

      <div className="rounded-2xl border border-ink-200 bg-white p-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
          <input
            type="search"
            placeholder="Buscar por nombre o key…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="w-full rounded-lg border border-ink-200 bg-white pl-9 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-ink-200 bg-white p-10 text-center text-ink-400">
          <Loader2 className="mx-auto mb-2 size-6 animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-white p-10 text-center">
          <FileSpreadsheet className="mx-auto mb-3 size-10 text-ink-300" />
          <p className="font-semibold text-ink-700">Sin proveedores aún</p>
          <p className="text-sm text-ink-500">Crea el primero arriba.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b border-ink-200 bg-ink-50 text-left text-xs uppercase text-ink-500">
              <tr>
                <th className="px-4 py-3">Proveedor</th>
                <th className="px-4 py-3">Key</th>
                <th className="px-4 py-3 text-right">Imports</th>
                <th className="px-4 py-3">Último import</th>
                <th className="px-4 py-3 text-center">Activo</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-ink-100 hover:bg-ink-50">
                  <td className="px-4 py-3 font-semibold text-ink-900">{s.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-500">{s.key}</td>
                  <td className="px-4 py-3 text-right">{s.importsCount}</td>
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {s.lastImportAt
                      ? new Date(s.lastImportAt).toLocaleString('es-MX')
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => toggleActive(s)}
                      title={
                        s.isActive
                          ? 'Activo — clic para desactivar y ocultar sus productos'
                          : 'Inactivo — clic para activar y mostrar sus productos'
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        s.isActive ? 'bg-emerald-500' : 'bg-ink-300'
                      }`}
                    >
                      <span
                        className={`inline-block size-4 transform rounded-full bg-white shadow transition-transform ${
                          s.isActive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        title="Editar configuración"
                        onClick={() => setEditingId(s.id)}
                        className="rounded p-1.5 text-ink-500 hover:bg-brand-50 hover:text-brand-700"
                      >
                        <Settings2 className="size-4" />
                      </button>
                      <button
                        title="Importar Excel"
                        onClick={() => setImporting(s)}
                        className="rounded p-1.5 text-ink-500 hover:bg-emerald-50 hover:text-emerald-700"
                      >
                        <Upload className="size-4" />
                      </button>
                      <button
                        title="Eliminar"
                        onClick={() => remove(s)}
                        className="rounded p-1.5 text-ink-500 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de creación */}
      {creating && (
        <CreateModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            setEditingId(id)
          }}
        />
      )}

      {/* Modal de importación */}
      {importing && (
        <ImportModal
          supplier={importing}
          onClose={() => {
            setImporting(null)
            load()
          }}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Create modal                                                                */
/* -------------------------------------------------------------------------- */

function CreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    setSaving(true)
    setError('')
    const r = await fetch('/api/admin/suppliers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        key: key.trim().toLowerCase(),
        name: name.trim(),
        columnMapping: {},
        headerRow: 0,
      }),
    })
    setSaving(false)
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      setError(d.error || `HTTP ${r.status}`)
      return
    }
    const d = await r.json()
    onCreated(d.id)
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-ink-900">Nuevo proveedor</h2>
          <button onClick={onClose} className="rounded p-1 text-ink-500 hover:bg-ink-100">
            <X className="size-5" />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-3"
        >
          <Field label="Nombre comercial *">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
              placeholder="Ej. Industrias del Norte"
            />
          </Field>
          <Field label="Key (slug interno) *">
            <input
              required
              pattern="[a-z0-9_-]+"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase())}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 font-mono text-sm"
              placeholder="ej. industrias_norte"
            />
            <span className="text-xs text-ink-500">
              Minúsculas, números, guion bajo o medio.
            </span>
          </Field>

          {error && (
            <div className="rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !key || !name}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Creando…' : 'Crear y configurar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Detail view (edit config)                                                   */
/* -------------------------------------------------------------------------- */

function SupplierDetailView({
  id,
  onBack,
}: {
  id: string
  onBack: () => void
}) {
  const [supplier, setSupplier] = useState<SupplierDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // detección de columnas
  const detectFileRef = useRef<HTMLInputElement | null>(null)
  const [detecting, setDetecting] = useState(false)
  const [detected, setDetected] = useState<{ sheets: string[]; details: SheetInfo[] } | null>(
    null,
  )

  // JSON raw expandible
  const [showRawJson, setShowRawJson] = useState(false)
  const [rawJsonText, setRawJsonText] = useState('')
  const [rawJsonError, setRawJsonError] = useState('')

  async function load() {
    setLoading(true)
    try {
      const r = await fetch(`/api/admin/suppliers/${id}`)
      const d = await r.json()
      setSupplier(d)
      setRawJsonText(JSON.stringify(d.columnMapping || {}, null, 2))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
  }, [id])

  async function save() {
    if (!supplier) return
    setSaving(true)
    setError('')
    setSuccess('')
    const payload = {
      name: supplier.name,
      contactEmail: supplier.contactEmail,
      contactPhone: supplier.contactPhone,
      sheetName: supplier.sheetName,
      headerRow: supplier.headerRow,
      columnMapping: supplier.columnMapping,
      transformConfig: supplier.transformConfig,
      skuPrefix: supplier.skuPrefix,
      notes: supplier.notes,
      isActive: supplier.isActive,
    }
    const r = await fetch(`/api/admin/suppliers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    setSaving(false)
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      setError(d.error || `HTTP ${r.status}`)
      return
    }
    setSuccess('Configuración guardada')
    setTimeout(() => setSuccess(''), 2500)
  }

  async function detectColumns(file: File) {
    setDetecting(true)
    setDetected(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch(`/api/admin/suppliers/${id}/detect-columns`, {
        method: 'POST',
        body: fd,
      })
      const d = await r.json()
      if (!r.ok) {
        setError(d.error || 'No se pudo detectar')
        return
      }
      setDetected({ sheets: d.sheets || [], details: d.details || [] })
    } finally {
      setDetecting(false)
    }
  }

  // Columnas disponibles según la hoja + header row escogidos
  const availableColumns = useMemo(() => {
    if (!detected || !supplier) return [] as string[]
    const sheetName = supplier.sheetName || detected.sheets[0]
    const sheet = detected.details.find((s) => s.name === sheetName)
    if (!sheet) return []
    return sheet.columnsByHeaderRow[supplier.headerRow] || []
  }, [detected, supplier?.sheetName, supplier?.headerRow])

  function setMapping(field: V2Field, value: string | string[] | null) {
    if (!supplier) return
    setSupplier({
      ...supplier,
      columnMapping: { ...supplier.columnMapping, [field]: value },
    })
  }

  function applyRawJson() {
    setRawJsonError('')
    try {
      const parsed = JSON.parse(rawJsonText)
      if (typeof parsed !== 'object' || parsed === null) {
        throw new Error('Debe ser un objeto JSON')
      }
      if (!supplier) return
      setSupplier({ ...supplier, columnMapping: parsed })
      setShowRawJson(false)
    } catch (e: any) {
      setRawJsonError(e?.message || 'JSON inválido')
    }
  }

  function setTransform<K extends keyof TransformConfig>(key: K, value: TransformConfig[K]) {
    if (!supplier) return
    const tc = { ...(supplier.transformConfig ?? {}) }
    if (value === undefined || value === null || (typeof value === 'string' && value === '')) {
      delete (tc as any)[key]
    } else {
      ;(tc as any)[key] = value
    }
    setSupplier({
      ...supplier,
      transformConfig: Object.keys(tc).length === 0 ? null : tc,
    })
  }

  if (loading || !supplier) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-sm text-brand-600">
          <ChevronLeft className="size-4" /> Volver
        </button>
        <div className="rounded-2xl border border-ink-200 bg-white p-10 text-center text-ink-400">
          <Loader2 className="mx-auto mb-2 size-6 animate-spin" /> Cargando…
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <button
            onClick={onBack}
            className="mb-2 inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
          >
            <ChevronLeft className="size-4" /> Volver a proveedores
          </button>
          <h1 className="text-2xl font-extrabold text-ink-900">{supplier.name}</h1>
          <p className="font-mono text-xs text-ink-500">key: {supplier.key}</p>
        </div>
        <div className="flex items-center gap-2">
          {success && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="size-3.5" /> {success}
            </span>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Guardar configuración'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mr-1 inline size-4" /> {error}
        </div>
      )}

      {/* Sección datos */}
      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-bold text-ink-900">Datos del proveedor</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Nombre comercial">
            <input
              value={supplier.name}
              onChange={(e) => setSupplier({ ...supplier, name: e.target.value })}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Email de contacto">
            <input
              type="email"
              value={supplier.contactEmail ?? ''}
              onChange={(e) =>
                setSupplier({ ...supplier, contactEmail: e.target.value || null })
              }
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Teléfono">
            <input
              value={supplier.contactPhone ?? ''}
              onChange={(e) =>
                setSupplier({ ...supplier, contactPhone: e.target.value || null })
              }
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Prefijo de SKU (opcional)">
            <input
              value={supplier.skuPrefix ?? ''}
              onChange={(e) =>
                setSupplier({ ...supplier, skuPrefix: e.target.value || null })
              }
              placeholder="Ej. AM-"
              className="w-full rounded-lg border border-ink-200 px-3 py-2 font-mono text-sm"
            />
          </Field>
          <Field label="Default brand_id (opcional)">
            <input
              value={supplier.defaultBrandId ?? ''}
              onChange={(e) =>
                setSupplier({
                  ...supplier,
                  defaultBrandId: e.target.value.replace(/[^\d]/g, '') || null,
                })
              }
              placeholder="ID numérico"
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Default category_id (opcional)">
            <input
              value={supplier.defaultCategoryId ?? ''}
              onChange={(e) =>
                setSupplier({
                  ...supplier,
                  defaultCategoryId: e.target.value.replace(/[^\d]/g, '') || null,
                })
              }
              placeholder="ID numérico"
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={supplier.isActive}
            onChange={(e) => setSupplier({ ...supplier, isActive: e.target.checked })}
          />
          Proveedor activo
        </label>
      </section>

      {/* Sección Excel */}
      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-bold text-ink-900">Configuración del Excel</h2>

        <div className="mb-4 rounded-lg border border-dashed border-ink-300 bg-ink-50 p-3">
          <p className="mb-2 text-xs font-semibold text-ink-700">
            Sube un XLSX de muestra para auto-detectar hojas y columnas:
          </p>
          <div className="flex items-center gap-2">
            <input
              ref={detectFileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) detectColumns(f)
              }}
              className="block w-full text-sm text-ink-700 file:mr-3 file:rounded file:border-0 file:bg-brand-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-700"
            />
            {detecting && <Loader2 className="size-4 animate-spin text-ink-500" />}
          </div>
          {detected && (
            <p className="mt-2 text-xs text-ink-600">
              {detected.sheets.length} hoja(s) detectada(s). Columnas disponibles abajo según la
              fila de headers seleccionada.
            </p>
          )}
        </div>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Hoja del workbook">
            {detected && detected.sheets.length > 0 ? (
              <select
                value={supplier.sheetName ?? ''}
                onChange={(e) =>
                  setSupplier({ ...supplier, sheetName: e.target.value || null })
                }
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">(primera hoja)</option>
                {detected.sheets.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={supplier.sheetName ?? ''}
                onChange={(e) =>
                  setSupplier({ ...supplier, sheetName: e.target.value || null })
                }
                placeholder="Nombre exacto de la hoja (opcional)"
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
              />
            )}
          </Field>
          <Field label="Fila de encabezados (0-indexed)">
            <input
              type="number"
              min={0}
              max={50}
              value={supplier.headerRow}
              onChange={(e) =>
                setSupplier({ ...supplier, headerRow: parseInt(e.target.value) || 0 })
              }
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <h3 className="mb-2 text-sm font-bold text-ink-800">Mapeo de columnas</h3>
        <div className="overflow-x-auto rounded-lg border border-ink-200">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left text-xs uppercase text-ink-500">
              <tr>
                <th className="px-3 py-2">Campo v2</th>
                <th className="px-3 py-2">Columna del Excel</th>
              </tr>
            </thead>
            <tbody>
              {V2_FIELDS.map((f) => (
                <MappingRow
                  key={f.key}
                  field={f.key}
                  label={f.label}
                  required={f.required}
                  multi={(f as any).multi}
                  value={supplier.columnMapping[f.key] ?? null}
                  options={availableColumns}
                  onChange={(v) => setMapping(f.key, v)}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => {
              setRawJsonText(JSON.stringify(supplier.columnMapping ?? {}, null, 2))
              setShowRawJson((v) => !v)
            }}
            className="text-xs font-semibold text-brand-600 hover:underline"
          >
            {showRawJson ? 'Ocultar JSON crudo' : 'Editar JSON crudo (avanzado)'}
          </button>
          {showRawJson && (
            <div className="mt-2 space-y-2">
              <textarea
                rows={10}
                value={rawJsonText}
                onChange={(e) => setRawJsonText(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-ink-50 p-2 font-mono text-xs"
              />
              {rawJsonError && (
                <div className="text-xs text-red-700">{rawJsonError}</div>
              )}
              <button
                onClick={applyRawJson}
                className="rounded bg-brand-100 px-3 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-200"
              >
                Aplicar JSON al mapping
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Sección transformaciones */}
      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-bold text-ink-900">Transformaciones</h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Multiplicador de precio (1 = sin cambio)">
            <input
              type="number"
              step="0.0001"
              min={0}
              value={supplier.transformConfig?.price_multiplier ?? ''}
              onChange={(e) =>
                setTransform(
                  'price_multiplier',
                  e.target.value ? parseFloat(e.target.value) : undefined,
                )
              }
              placeholder="Ej. 1.16 si el Excel viene sin IVA"
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
            />
          </Field>

          <Field label="Normalización de SKU">
            <select
              value={supplier.transformConfig?.normalize_sku ?? ''}
              onChange={(e) =>
                setTransform(
                  'normalize_sku',
                  (e.target.value || undefined) as TransformConfig['normalize_sku'],
                )
              }
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">trim + colapsar espacios (default)</option>
              <option value="trim">solo trim</option>
              <option value="upper">solo mayúsculas</option>
              <option value="trim_upper">trim + mayúsculas (combo)</option>
            </select>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Combinar columnas de stock (separadas por coma)">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
              <input
                value={(supplier.transformConfig?.stock_combine_columns ?? []).join(', ')}
                onChange={(e) => {
                  const cols = e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                  setTransform(
                    'stock_combine_columns',
                    cols.length > 0 ? cols : undefined,
                  )
                }}
                placeholder="Ej: A, DF, GDL"
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm"
              />
              <select
                value={supplier.transformConfig?.stock_combine_op ?? 'sum'}
                onChange={(e) =>
                  setTransform(
                    'stock_combine_op',
                    e.target.value as TransformConfig['stock_combine_op'],
                  )
                }
                className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                <option value="sum">sumar</option>
                <option value="max">máximo</option>
              </select>
            </div>
            <span className="text-xs text-ink-500">
              Si se llena, ignora el mapping de stock y suma/maximiza estas columnas.
            </span>
          </Field>
        </div>
      </section>
    </div>
  )
}

function MappingRow({
  field,
  label,
  required,
  multi,
  value,
  options,
  onChange,
}: {
  field: V2Field
  label: string
  required: boolean
  multi?: boolean
  value: string | string[] | null
  options: string[]
  onChange: (v: string | string[] | null) => void
}) {
  const isArr = Array.isArray(value)
  const [useMulti, setUseMulti] = useState(isArr)

  // Si hay options, mostrar select; si no, input libre
  const hasOptions = options.length > 0

  return (
    <tr className="border-t border-ink-100">
      <td className="px-3 py-2 align-top text-ink-800">
        <span className="font-semibold">{label}</span>
        {required && <span className="ml-1 text-xs text-red-600">*</span>}
        <div className="font-mono text-[10px] text-ink-400">{field}</div>
      </td>
      <td className="px-3 py-2">
        {multi || useMulti ? (
          <div className="space-y-1">
            <input
              value={Array.isArray(value) ? value.join(', ') : value ?? ''}
              onChange={(e) => {
                const parts = e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                onChange(parts.length === 0 ? null : parts)
              }}
              placeholder="Columnas separadas por coma (se concatenan)"
              className="w-full rounded border border-ink-200 px-2 py-1 text-sm"
            />
            {hasOptions && (
              <div className="flex flex-wrap gap-1 text-[10px]">
                {options.slice(0, 12).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      const cur = Array.isArray(value) ? value : []
                      if (!cur.includes(opt)) onChange([...cur, opt])
                    }}
                    className="rounded bg-ink-100 px-1.5 py-0.5 hover:bg-brand-100 hover:text-brand-700"
                  >
                    + {opt}
                  </button>
                ))}
              </div>
            )}
            {!multi && (
              <button
                type="button"
                onClick={() => {
                  setUseMulti(false)
                  onChange(null)
                }}
                className="text-[10px] text-ink-500 hover:underline"
              >
                volver a columna única
              </button>
            )}
          </div>
        ) : hasOptions ? (
          <div className="flex items-center gap-2">
            <select
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => onChange(e.target.value || null)}
              className="w-full rounded border border-ink-200 bg-white px-2 py-1 text-sm"
            >
              <option value="">— sin mapear —</option>
              {options.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setUseMulti(true)}
              title="Combinar varias columnas"
              className="rounded p-1 text-ink-500 hover:bg-ink-100"
            >
              +
            </button>
          </div>
        ) : (
          <input
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder="Nombre exacto de la columna"
            className="w-full rounded border border-ink-200 px-2 py-1 text-sm"
          />
        )}
      </td>
    </tr>
  )
}

/* -------------------------------------------------------------------------- */
/* Import modal                                                                */
/* -------------------------------------------------------------------------- */

function ImportModal({
  supplier,
  onClose,
}: {
  supplier: SupplierListItem
  onClose: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [preview, setPreview] = useState<any | null>(null)
  const [applying, setApplying] = useState(false)
  const [result, setResult] = useState<any | null>(null)
  const [error, setError] = useState('')

  async function runPreview() {
    if (!file) return
    setPreviewing(true)
    setError('')
    setPreview(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch(`/api/admin/suppliers/${supplier.id}/preview`, {
        method: 'POST',
        body: fd,
      })
      const d = await r.json()
      if (!r.ok) {
        setError(d.error || `HTTP ${r.status}`)
        return
      }
      setPreview(d)
    } finally {
      setPreviewing(false)
    }
  }

  async function apply() {
    if (!file || !preview) return
    if (
      !confirm(
        `¿Aplicar la importación?\n\nCrea ${preview.toCreateCount} y actualiza ${preview.toUpdateCount} productos. No se puede deshacer fácilmente.`,
      )
    )
      return
    setApplying(true)
    setError('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('confirmed', 'true')
      const r = await fetch(`/api/admin/suppliers/${supplier.id}/apply`, {
        method: 'POST',
        body: fd,
      })
      const d = await r.json()
      if (!r.ok) {
        setError(d.error || `HTTP ${r.status}`)
        return
      }
      setResult(d)
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-ink-900">
              Importar Excel — {supplier.name}
            </h2>
            <p className="font-mono text-xs text-ink-500">key: {supplier.key}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-ink-500 hover:bg-ink-100">
            <X className="size-5" />
          </button>
        </div>

        {!result && (
          <>
            <div className="mb-4 rounded-lg border border-dashed border-ink-300 bg-ink-50 p-4 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null)
                  setPreview(null)
                }}
                className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-brand-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-brand-700"
              />
              {file && (
                <p className="mt-2 text-xs text-ink-600">
                  Archivo: <span className="font-mono">{file.name}</span> (
                  {(file.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>

            <div className="mb-4 flex gap-2">
              <button
                onClick={runPreview}
                disabled={!file || previewing}
                className="inline-flex items-center gap-2 rounded-lg bg-ink-800 px-4 py-2 text-sm font-semibold text-white hover:bg-ink-900 disabled:opacity-50"
              >
                {previewing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                Vista previa
              </button>
              {preview && (
                <button
                  onClick={apply}
                  disabled={applying || preview.toCreateCount + preview.toUpdateCount === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {applying ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  Aplicar importación
                </button>
              )}
            </div>

            {error && (
              <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertTriangle className="mr-1 inline size-4" /> {error}
              </div>
            )}

            {preview && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Stat label="Total filas" value={preview.totalRows} />
                  <Stat label="A crear" value={preview.toCreateCount} accent="emerald" />
                  <Stat label="A actualizar" value={preview.toUpdateCount} accent="brand" />
                  <Stat label="Inválidas" value={preview.invalidCount} accent="red" />
                </div>

                {preview.sample.toCreate.length > 0 && (
                  <SamplePanel
                    title={`Muestra a crear (primeras ${preview.sample.toCreate.length})`}
                    rows={preview.sample.toCreate}
                  />
                )}
                {preview.sample.toUpdate.length > 0 && (
                  <SamplePanel
                    title={`Muestra a actualizar (primeras ${preview.sample.toUpdate.length})`}
                    rows={preview.sample.toUpdate}
                  />
                )}
                {preview.sample.invalid.length > 0 && (
                  <SamplePanel
                    title={`Filas inválidas (primeras ${preview.sample.invalid.length})`}
                    rows={preview.sample.invalid}
                  />
                )}
              </div>
            )}
          </>
        )}

        {result && (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-50 p-4 text-emerald-700">
              <CheckCircle2 className="mr-1 inline size-5" /> Importación aplicada
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Total filas" value={result.totalRows} />
              <Stat label="Creadas" value={result.created} accent="emerald" />
              <Stat label="Actualizadas" value={result.updated} accent="brand" />
              <Stat label="Fallidas" value={result.failed} accent="red" />
            </div>
            {result.sample?.errors?.length > 0 && (
              <SamplePanel title="Errores" rows={result.sample.errors} />
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Mini components                                                             */
/* -------------------------------------------------------------------------- */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-ink-700">{label}</span>
      {children}
    </label>
  )
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string
  value: number | string
  accent?: 'emerald' | 'brand' | 'red'
}) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700',
    brand: 'bg-brand-50 text-brand-700',
    red: 'bg-red-50 text-red-700',
  } as const
  const cls = accent ? colors[accent] : 'bg-ink-50 text-ink-700'
  return (
    <div className={`rounded-lg p-3 ${cls}`}>
      <div className="text-xs font-semibold uppercase opacity-75">{label}</div>
      <div className="text-xl font-extrabold">{value}</div>
    </div>
  )
}

function SamplePanel({ title, rows }: { title: string; rows: any[] }) {
  if (!rows || rows.length === 0) return null
  const keys = Array.from(
    new Set(rows.flatMap((r) => Object.keys(r ?? {}))),
  )
  return (
    <div className="rounded-lg border border-ink-200">
      <div className="flex items-center gap-2 border-b border-ink-200 bg-ink-50 px-3 py-2 text-xs font-bold text-ink-800">
        <ListOrdered className="size-3.5" /> {title}
      </div>
      <div className="max-h-60 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-white">
            <tr>
              {keys.map((k) => (
                <th key={k} className="px-2 py-1 text-left text-[10px] uppercase text-ink-500">
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-ink-100">
                {keys.map((k) => (
                  <td key={k} className="px-2 py-1 font-mono text-[11px] text-ink-700">
                    {String(r?.[k] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default Proveedores
